import { useState, useCallback } from 'react';
import { FixtureData } from '@graphql/generated/graphql';
import { ZoneCreationProperties } from '../types/zoneMapType';

/**
 * Selection mode for fixture selection
 */
export enum SelectionMode {
  CLICK = 'click',
  RECTANGLE = 'rectangle',
}

/**
 * Modifier keys state for multi-select operations
 */
export interface ModifierKeys {
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
}

/**
 * Selection history entry for undo functionality
 */
export interface SelectionHistoryEntry {
  zoneIdx: number;
  fixtureIds: string[];
  timestamp: number;
  operation: 'add' | 'remove';
}

/**
 * Props for useFixtureSelection hook
 */
export interface UseFixtureSelectionProps {
  /** Map of all zones being created/edited */
  zoneCreationProperties: Map<number, ZoneCreationProperties>;
  /** Setter for zone creation properties */
  setZoneCreationProperties: React.Dispatch<React.SetStateAction<Map<number, ZoneCreationProperties>>>;
  /** Global list of all selected fixtures across all zones */
  selectedFixtures: string[];
  /** Setter for selected fixtures */
  setSelectedFixtures: React.Dispatch<React.SetStateAction<string[]>>;
  /** List of all available agile fixtures from the floorplan */
  agileFixtures: FixtureData[];
  /**
   * Index of the currently active zone that will receive fixture selections.
   * This is managed by the parent component (ZoneViewer) and determines which
   * zone fixtures are assigned to when selected on the floorplan.
   *
   * Users can change the active zone by:
   * - Clicking on a zone card in the sidebar
   * - Using the zone selector dropdown in the toolbar
   * - Creating a new zone (automatically becomes active)
   */
  activeZoneIdx: number;
}

/**
 * Selection result for providing feedback
 */
export interface SelectionResult {
  success: boolean;
  selectedCount: number;
  filteredCount: number;
  error?: string;
}

/**
 * Return type for useFixtureSelection hook
 */
export interface UseFixtureSelectionReturn {
  // Selection state
  tempSelection: Set<string>;
  selectionMode: SelectionMode;

  // Selection operations
  handleFixtureClick: (fixtureId: string, modifierKeys: ModifierKeys) => SelectionResult | null;
  handleRectangleSelection: (fixtureIds: string[]) => SelectionResult;
  clearTempSelection: () => void;
  commitSelection: () => SelectionResult;

  // Undo operations
  undoLastSelection: () => void;
  canUndo: boolean;

  // Mode management
  setSelectionMode: (mode: SelectionMode) => void;

  // Validation
  getFixtureAssignedZone: (fixtureId: string) => string | null;
}

/**
 * Custom hook to manage fixture selection state and operations
 *
 * This hook provides functionality for:
 * - Single fixture click selection
 * - Multi-select with modifier keys (Shift, Ctrl/Cmd)
 * - Rectangle/lasso selection
 * - Fixture availability validation
 * - Selection history and undo
 *
 * ## Active Zone Concept
 * The "active zone" is the zone that will receive any fixtures selected on the floorplan.
 * Only one zone can be active at a time. The parent component (ZoneViewer) manages which
 * zone is active and passes the activeZoneIdx to this hook.
 *
 * When a user selects fixtures (via click or rectangle selection), those fixtures are
 * assigned to the currently active zone. This prevents accidentally assigning fixtures
 * to the wrong zone when working with multiple zones.
 *
 * @param props - Configuration for fixture selection
 * @returns Selection state and operations
 */
export const useFixtureSelection = (props: UseFixtureSelectionProps): UseFixtureSelectionReturn => {
  const { zoneCreationProperties, setZoneCreationProperties, setSelectedFixtures, agileFixtures, activeZoneIdx } =
    props;

  // Temporary selection state during multi-select operations
  const [tempSelection, setTempSelection] = useState<Set<string>>(new Set());

  // Current selection mode
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(SelectionMode.CLICK);

  // Selection history for undo functionality
  const [selectionHistory, setSelectionHistory] = useState<SelectionHistoryEntry[]>([]);

  /**
   * Get the zone name that a fixture is assigned to (if any)
   * Returns null if fixture is not assigned to any zone
   */
  const getFixtureAssignedZone = useCallback(
    (fixtureId: string): string | null => {
      for (const [zoneIdx, zone] of zoneCreationProperties.entries()) {
        if (zone.zoneIdx !== activeZoneIdx && zone.fixtureIds.includes(fixtureId)) {
          return zone.zoneName || `Zone ${zoneIdx + 1}`;
        }
      }
      return null;
    },
    [zoneCreationProperties, activeZoneIdx],
  );

  /**
   * Check if a fixture is available for selection
   * A fixture is available if:
   * 1. It exists in the agileFixtures list
   * 2. It's not already assigned to another zone
   * 3. Or it's already assigned to the current active zone
   */
  const isFixtureAvailable = useCallback(
    (fixtureId: string): boolean => {
      // Check if fixture exists in agile fixtures
      const fixtureExists = agileFixtures.some((fixture) => fixture.id === fixtureId);
      if (!fixtureExists) {
        return false;
      }

      // Check if fixture is already assigned to another zone
      const currentZone = zoneCreationProperties.get(activeZoneIdx);
      if (!currentZone) {
        return false;
      }

      // If fixture is in current zone, it's available
      if (currentZone.fixtureIds.includes(fixtureId)) {
        return true;
      }

      // Check if fixture is assigned to any other zone
      return getFixtureAssignedZone(fixtureId) === null;
    },
    [agileFixtures, zoneCreationProperties, activeZoneIdx, getFixtureAssignedZone],
  );

  /**
   * Handle single fixture click selection
   * Simple toggle behavior - click to add/remove fixture from active zone
   */
  const handleFixtureClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (fixtureId: string, _modifierKeys: ModifierKeys): SelectionResult | null => {
      const currentZone = zoneCreationProperties.get(activeZoneIdx);
      if (!currentZone) {
        console.error(`Active zone ${activeZoneIdx} not found`);
        return {
          success: false,
          selectedCount: 0,
          filteredCount: 0,
          error: 'Active zone not found',
        };
      }

      const isCurrentlySelected = currentZone.fixtureIds.includes(fixtureId);

      // If removing, always allow it
      if (isCurrentlySelected) {
        const newFixtureIds = currentZone.fixtureIds.filter((id) => id !== fixtureId);

        // Update zone creation properties
        setZoneCreationProperties((prev) => {
          const newMap = new Map(prev);
          newMap.set(activeZoneIdx, {
            ...currentZone,
            fixtureIds: newFixtureIds,
          });
          return newMap;
        });

        // Update selected fixtures
        setSelectedFixtures((prev) => prev.filter((id) => id !== fixtureId));

        // Add to selection history for undo
        setSelectionHistory((prev) => [
          ...prev,
          {
            zoneIdx: activeZoneIdx,
            fixtureIds: [fixtureId],
            timestamp: Date.now(),
            operation: 'remove',
          },
        ]);

        return null; // No feedback needed for removal
      }

      // Validate fixture availability for adding
      if (!isFixtureAvailable(fixtureId)) {
        const assignedZone = getFixtureAssignedZone(fixtureId);
        return {
          success: false,
          selectedCount: 0,
          filteredCount: 1,
          error: assignedZone ? `Fixture is already assigned to ${assignedZone}` : 'Fixture is not available',
        };
      }

      // Add fixture
      const newFixtureIds = [...currentZone.fixtureIds, fixtureId];

      // Update zone creation properties
      setZoneCreationProperties((prev) => {
        const newMap = new Map(prev);
        newMap.set(activeZoneIdx, {
          ...currentZone,
          fixtureIds: newFixtureIds,
        });
        return newMap;
      });

      // Update selected fixtures
      setSelectedFixtures((prev) => [...prev, fixtureId]);

      // Add to selection history for undo
      setSelectionHistory((prev) => [
        ...prev,
        {
          zoneIdx: activeZoneIdx,
          fixtureIds: [fixtureId],
          timestamp: Date.now(),
          operation: 'add',
        },
      ]);

      return null; // No feedback needed for single selection
    },
    [
      isFixtureAvailable,
      getFixtureAssignedZone,
      zoneCreationProperties,
      activeZoneIdx,
      setZoneCreationProperties,
      setSelectedFixtures,
    ],
  );

  /**
   * Handle rectangle selection
   * Filters out already-assigned fixtures
   */
  const handleRectangleSelection = useCallback(
    (fixtureIds: string[]): SelectionResult => {
      // Filter to only available fixtures
      const availableFixtureIds = fixtureIds.filter(isFixtureAvailable);
      const filteredCount = fixtureIds.length - availableFixtureIds.length;

      if (availableFixtureIds.length === 0) {
        return {
          success: false,
          selectedCount: 0,
          filteredCount,
          error: 'All selected fixtures are already assigned to other zones',
        };
      }

      // Add to temp selection
      setTempSelection(new Set(availableFixtureIds));

      return {
        success: true,
        selectedCount: availableFixtureIds.length,
        filteredCount,
      };
    },
    [isFixtureAvailable],
  );

  /**
   * Clear temporary selection
   */
  const clearTempSelection = useCallback(() => {
    setTempSelection(new Set());
  }, []);

  /**
   * Commit temporary selection to the active zone
   */
  const commitSelection = useCallback((): SelectionResult => {
    if (tempSelection.size === 0) {
      return {
        success: false,
        selectedCount: 0,
        filteredCount: 0,
        error: 'No fixtures selected',
      };
    }

    const currentZone = zoneCreationProperties.get(activeZoneIdx);
    if (!currentZone) {
      console.error(`Active zone ${activeZoneIdx} not found`);
      return {
        success: false,
        selectedCount: 0,
        filteredCount: 0,
        error: 'Active zone not found',
      };
    }

    const fixturesToAdd = Array.from(tempSelection);
    const newFixtureIds = [...currentZone.fixtureIds, ...fixturesToAdd];

    // Update zone creation properties
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(activeZoneIdx, {
        ...currentZone,
        fixtureIds: newFixtureIds,
      });
      return newMap;
    });

    // Update selected fixtures
    setSelectedFixtures((prev) => [...prev, ...fixturesToAdd]);

    // Add to selection history
    setSelectionHistory((prev) => [
      ...prev,
      {
        zoneIdx: activeZoneIdx,
        fixtureIds: fixturesToAdd,
        timestamp: Date.now(),
        operation: 'add',
      },
    ]);

    // Clear temp selection
    setTempSelection(new Set());

    return {
      success: true,
      selectedCount: fixturesToAdd.length,
      filteredCount: 0,
    };
  }, [tempSelection, zoneCreationProperties, activeZoneIdx, setZoneCreationProperties, setSelectedFixtures]);

  /**
   * Undo the last selection operation
   */
  const undoLastSelection = useCallback(() => {
    if (selectionHistory.length === 0) {
      return;
    }

    const lastEntry = selectionHistory[selectionHistory.length - 1];
    const { zoneIdx, fixtureIds, operation } = lastEntry;

    const zone = zoneCreationProperties.get(zoneIdx);
    if (!zone) {
      console.error(`Zone ${zoneIdx} not found for undo operation`);
      return;
    }

    // Reverse the operation
    const newFixtureIds =
      operation === 'add'
        ? zone.fixtureIds.filter((id) => !fixtureIds.includes(id))
        : [...zone.fixtureIds, ...fixtureIds];

    // Update zone creation properties
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(zoneIdx, {
        ...zone,
        fixtureIds: newFixtureIds,
      });
      return newMap;
    });

    // Update selected fixtures
    setSelectedFixtures((prev) => {
      if (operation === 'add') {
        return prev.filter((id) => !fixtureIds.includes(id));
      } else {
        return [...prev, ...fixtureIds];
      }
    });

    // Remove last entry from history
    setSelectionHistory((prev) => prev.slice(0, -1));
  }, [selectionHistory, zoneCreationProperties, setZoneCreationProperties, setSelectedFixtures]);

  return {
    // Selection state
    tempSelection,
    selectionMode,

    // Selection operations
    handleFixtureClick,
    handleRectangleSelection,
    clearTempSelection,
    commitSelection,

    // Undo operations
    undoLastSelection,
    canUndo: selectionHistory.length > 0,

    // Mode management
    setSelectionMode,

    // Validation
    getFixtureAssignedZone,
  };
};
